import { FileEnums } from "models/enums";

/**
 * Checks if a content type is HEIC or HEIF
 * @param contentType - MIME type to check
 * @returns true if the content type is HEIC or HEIF
 */
export const isHeicOrHeif = (contentType?: string): boolean => {
    if (!contentType) {
        return false;
    }
    const lowerContentType = contentType.toLowerCase();
    return (
        lowerContentType === FileEnums.ContentTypes.ImageHEIC ||
        lowerContentType === FileEnums.ContentTypes.ImageHEIF
    );
};

/**
 * Converts HEIC/HEIF base64 data to JPEG base64 data
 * The heic-to library is lazy loaded to reduce initial bundle size
 * @param base64Data - Base64 encoded HEIC/HEIF image data
 * @returns Promise resolving to base64 encoded JPEG image data
 * @throws Error if conversion fails or data is invalid
 */
export const convertHeicToJpeg = async (base64Data: string): Promise<string> => {
    if (!base64Data || base64Data.length === 0) {
        throw new Error("Empty base64 data provided");
    }

    // Lazy load heic-to library (large package, only load when needed)
    const { heicTo } = await import("heic-to");

    // Convert base64 to Blob
    let binaryString: string;
    try {
        binaryString = window.atob(base64Data);
    } catch (error) {
        throw new Error("Invalid base64 data format");
    }
    // Use Uint8Array.from for efficient binary string to Uint8Array conversion
    // This is more performant than a manual loop as the browser can optimize it better
    const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
    const heicBlob = new Blob([bytes], { type: "image/heic" });

    // Convert HEIC to JPEG using heic-to
    const jpegBlob = await heicTo({
        blob: heicBlob,
        type: "image/jpeg",
        quality: 0.8,
    });

    // Convert Blob back to base64
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result as string;
            const convertedBase64 = base64String.split(",")[1];
            if (!convertedBase64) {
                reject(new Error("Failed to extract base64 data"));
                return;
            }
            resolve(convertedBase64);
        };
        reader.onerror = () => reject(new Error("Failed to read JPEG blob as data URL"));
        reader.readAsDataURL(jpegBlob);
    });
};

